/**
 * Split — a two-column row: a figure beside prose (eyebrow + heading + body),
 * stacking on mobile. This is the primitive that turns a stacked "prose then
 * image" flow into a structured row. Composes Figure + Prose + optional CTA.
 * Tokens + Tailwind only.
 */
import { Button } from '../button';
import { MarketingLink } from '../marketing-link';
import { Figure } from './Figure';
import type { FigureProps } from './Figure';
import { Prose } from './Prose';
import { cn } from '../../utils';

export interface SplitProps {
  readonly media: FigureProps;
  readonly eyebrow?: string;
  readonly title?: string;
  /** Body copy (markdown). */
  readonly content: string;
  /** Place the media on the right (default) or left. */
  readonly reverse?: boolean;
  readonly cta?: { readonly label: string; readonly href: string };
}

export function Split({ media, eyebrow, title, content, reverse = false, cta }: SplitProps) {
  return (
    <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-2 md:gap-12">
      <div className={cn(reverse && 'md:order-2')}>
        <Figure {...media} bare />
      </div>
      <div className={cn(reverse && 'md:order-1')}>
        {eyebrow && <p className="mb-3 font-mono text-sm uppercase tracking-wider text-primary">{eyebrow}</p>}
        {title && <h2 className="mb-4 font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">{title}</h2>}
        <Prose content={content} />
        {cta && (
          <Button asChild className="mt-6">
            <MarketingLink href={cta.href}>{cta.label}</MarketingLink>
          </Button>
        )}
      </div>
    </div>
  );
}
